import { useMemo, useState, useEffect, type MouseEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Users } from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { usePatientsList, type PatientListItem, type PatientStatus } from '@/hooks/usePatients';
import {
  DEFAULT_PATIENT_LIST_SORT,
  nextPatientListSort,
  sortPatientList,
  type PatientListSort,
  type PatientListSortColumn,
} from '@/lib/patientListSort';
import { formatPatientTelephoneNumbers } from '@/lib/patientStamkortFields';
import { MASKED_CPR } from '@/lib/cprDisplay';
import { usePatientAlertSet } from '@/hooks/useClinicalAlerts';
import { Avatar } from '@/components/ui/Avatar';
import { ColumnSortHeader } from '@/components/ui/ColumnSortHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Input } from '@/components/ui/Input';
import { ResponsiveTable } from '@/components/ui/Table';
import { Skeleton } from '@/components/ui/Skeleton';
import { Toggle } from '@/components/ui/Toggle';
import { PageLayout } from '@/components/ui/PageLayout';
import { PAGE_SUBTITLE, PAGE_TITLE_INLINE } from '@/components/ui/pageChrome';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

/**
 * Patients list page. Mirrors the v1 layout
 * (`Clinio/src/pages/clinic/Patients.tsx`):
 *   - Page header with patient count + Show archived toggle + Ny patient button
 *   - Search field (live filter)
 *   - Table: Name | CPR | Email | Telefon
 *   - Column headers sort the rows already loaded. Search, “only mine” and
 *     archived still filter on the server; sorting applies to that result.
 *
 * Differences from v1:
 *   - CPR column is always masked (`••••••-••••`) because v2 stores CPR encrypted.
 *   - Search hits a server-side FTS index (search_tsv) instead of filtering
 *     client-side.
 */
export function Patients() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId, isMainClinicAdmin, subClinics } = useActiveClinic();

  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [showSubClinicPatients, setShowSubClinicPatients] = useState(() => {
    try {
      const saved = localStorage.getItem('clinio_show_subclinic_patients');
      return saved === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('clinio_show_subclinic_patients', String(showSubClinicPatients));
    } catch {
      // localStorage not available
    }
  }, [showSubClinicPatients]);

  const { user, hasPermission } = useAuth();
  const userId = user?.id ?? null;
  const canUseMyFilter = Boolean(
    activeClinicId && hasPermission(activeClinicId, 'patient_my_filter', 'view'),
  );
  const [onlyMine, setOnlyMine] = useState(false);
  useEffect(() => {
    if (!userId) return;
    try {
      setOnlyMine(localStorage.getItem(onlyMyPatientsStorageKey(userId)) === 'true');
    } catch {
      setOnlyMine(false);
    }
  }, [userId]);
  const changeOnlyMine = (value: boolean) => {
    setOnlyMine(value);
    if (!userId) return;
    try {
      localStorage.setItem(onlyMyPatientsStorageKey(userId), String(value));
    } catch {
      // localStorage not available
    }
  };

  const clinicIds = useMemo(() => {
    if (!activeClinicId) return null;
    if (showSubClinicPatients && subClinics.length > 0) {
      return [activeClinicId, ...subClinics.map((c) => c.id)];
    }
    return activeClinicId;
  }, [activeClinicId, showSubClinicPatients, subClinics]);

  const filters = useMemo(
    () => ({
      search: searchTerm.trim() || undefined,
      status: (showArchived ? 'archived' : 'active') as PatientStatus,
      assignedToUserId: onlyMine && canUseMyFilter && userId ? userId : undefined,
    }),
    [searchTerm, showArchived, onlyMine, canUseMyFilter, userId],
  );

  const { data: patients, isLoading, error } = usePatientsList(clinicIds, filters);
  const alertPatients = usePatientAlertSet(activeClinicId);
  const [sort, setSort] = useState<PatientListSort>(DEFAULT_PATIENT_LIST_SORT);
  const clinicColumnVisible = showSubClinicPatients && subClinics.length > 0;
  const visibleSort =
    sort.column === 'clinic' && !clinicColumnVisible ? DEFAULT_PATIENT_LIST_SORT : sort;
  const sortedPatients = useMemo(
    () => sortPatientList(patients ?? [], visibleSort),
    [patients, visibleSort],
  );

  const count = patients?.length ?? 0;

  const sortHeader = (column: PatientListSortColumn, label: string) => {
    const active = visibleSort.column === column;
    return (
      <ColumnSortHeader
        label={label}
        active={active}
        direction={visibleSort.direction}
        onClick={() => setSort((current) => nextPatientListSort(current, column))}
        ariaLabel={
          active
            ? t(visibleSort.direction === 'asc' ? 'patients.sorted_asc' : 'patients.sorted_desc', {
                column: label,
              })
            : t('patients.sort_by', { column: label })
        }
      />
    );
  };

  const sortColumns: { id: PatientListSortColumn; label: string }[] = [
    { id: 'name', label: t('patients.col_name') },
    { id: 'cpr', label: t('patients.col_cpr') },
    { id: 'email', label: t('patients.col_email') },
    { id: 'phone', label: t('patients.col_phone') },
  ];
  if (clinicColumnVisible) {
    sortColumns.push({ id: 'clinic', label: t('patients.col_source_clinic') });
  }

  return (
    <PageLayout>
      <div className="flex min-h-10 w-full min-w-0 flex-wrap items-start justify-between gap-s3 nav:flex-nowrap">
        <div className={cn(PAGE_TITLE_INLINE, 'w-auto flex-none shrink-0')}>
          <h1 className={cn(TYPE.title, 'text-ds-text')}>{t('patients.title')}</h1>
          <span className={cn(PAGE_SUBTITLE, 'whitespace-nowrap')}>
            {showArchived
              ? t('patients.count_archived', { count })
              : t('patients.count_active', { count })}
          </span>
        </div>
        <div className="w-full min-w-0 max-w-search nav:w-[420px] nav:min-w-[12.5rem] nav:flex-1">
          <Input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder={t('patients.search_placeholder')}
            aria-label={t('patients.search_placeholder')}
          />
        </div>
        <div className="flex flex-wrap items-center gap-s3 nav:shrink-0 nav:flex-nowrap">
          {isMainClinicAdmin && subClinics.length > 0 ? (
            <Toggle
              checked={showSubClinicPatients}
              onCheckedChange={setShowSubClinicPatients}
              label={t('patients.show_subclinic_patients')}
            />
          ) : null}
          {canUseMyFilter ? (
            <Toggle
              checked={onlyMine}
              onCheckedChange={changeOnlyMine}
              disabled={!userId}
              label={t('patients.only_my_patients')}
            />
          ) : null}
          <Button
            type="button"
            variant="secondary"
            onClick={() => setShowArchived((value) => !value)}
          >
            {showArchived ? t('patients.show_active') : t('patients.show_archived')}
          </Button>
          {!showArchived ? (
            <Button onClick={() => navigate('/patients/new')} disabled={!activeClinicId}>
              {t('patients.new_patient')}
            </Button>
          ) : null}
        </div>
      </div>
      <div className="mt-s5">
        {error ? (
          <div className="mb-s4">
            <ErrorAlert message={t('patients.error_loading')} />
          </div>
        ) : null}

        {isLoading ? (
          <Card>
            <div className="flex flex-col gap-s3" aria-busy>
              {Array.from({ length: 6 }, (_, index) => (
                <Skeleton key={index} className="h-10 w-full" />
              ))}
            </div>
          </Card>
        ) : !patients || patients.length === 0 ? (
          <Card>
            <EmptyState
              icon={<Users className="h-7 w-7" />}
              title={
                searchTerm
                  ? t('patients.empty_search_title')
                  : onlyMine
                    ? t('patients.empty_mine_title')
                    : showArchived
                      ? t('patients.empty_archived_title')
                      : t('patients.empty_title')
              }
              description={
                searchTerm
                  ? t('patients.empty_search_subtitle')
                  : onlyMine
                    ? t('patients.empty_mine_subtitle')
                    : showArchived
                      ? t('patients.empty_archived_subtitle')
                      : t('patients.empty_subtitle')
              }
              action={
                !searchTerm && !onlyMine && !showArchived ? (
                  <Button onClick={() => navigate('/patients/new')} disabled={!activeClinicId}>
                    {t('patients.new_patient')}
                  </Button>
                ) : undefined
              }
            />
          </Card>
        ) : (
          <Card className="p-0 nav:p-0">
            <div className="flex flex-wrap gap-x-s4 gap-y-s2 border-b border-ds-line px-s3 py-s3 nav:hidden">
              {sortColumns.map((column) => (
                <span key={column.id}>{sortHeader(column.id, column.label)}</span>
              ))}
            </div>
            <ResponsiveTable
              rows={sortedPatients}
              getRowKey={(row) => row.id}
              getRowHref={(row) => `/patients/${row.id}`}
              getRowLabel={(row) => `${row.first_name} ${row.last_name}`.trim()}
              empty={null}
              columns={[
                {
                  id: 'name',
                  header: sortHeader('name', t('patients.col_name')),
                  sortDirection: ariaSort(visibleSort, 'name'),
                  cell: (row) => <NameCell patient={row} hasAlert={alertPatients.has(row.id)} />,
                },
                {
                  id: 'cpr',
                  header: sortHeader('cpr', t('patients.col_cpr')),
                  sortDirection: ariaSort(visibleSort, 'cpr'),
                  cell: (row) => (
                    <span
                      className="tabular-nums text-ds-muted"
                      aria-label={t('patients.cpr_modal.hidden_label')}
                    >
                      {row.has_cpr ? MASKED_CPR : '—'}
                    </span>
                  ),
                },
                {
                  id: 'email',
                  header: sortHeader('email', t('patients.col_email')),
                  sortDirection: ariaSort(visibleSort, 'email'),
                  cell: (row) => row.email ?? '—',
                  hideOnMobile: true,
                },
                {
                  id: 'phone',
                  header: sortHeader('phone', t('patients.col_phone')),
                  sortDirection: ariaSort(visibleSort, 'phone'),
                  cell: (row) =>
                    formatPatientTelephoneNumbers(row.telephone_subscribers, row.phone) || '—',
                },
                ...(clinicColumnVisible
                  ? [
                      {
                        id: 'clinic',
                        header: sortHeader('clinic', t('patients.col_source_clinic')),
                        sortDirection: ariaSort(visibleSort, 'clinic'),
                        cell: (row: PatientListItem) => row.clinic_name ?? '—',
                        hideOnMobile: true,
                      },
                    ]
                  : []),
              ]}
              mobileCard={(row) => (
                <div className="flex w-full items-start gap-s3 text-left">
                  <NameCell patient={row} hasAlert={alertPatients.has(row.id)} />
                </div>
              )}
            />
          </Card>
        )}
      </div>
    </PageLayout>
  );
}

function NameCell({ patient, hasAlert }: { patient: PatientListItem; hasAlert: boolean }) {
  const name = `${patient.first_name} ${patient.last_name}`.trim();
  const href = `/patients/${patient.id}`;
  return (
    <span className="inline-flex min-w-0 items-center gap-s3">
      <Avatar name={name} size={36} />
      <span className="min-w-0">
        <Link
          to={href}
          tabIndex={-1}
          onClick={stopRowInteractive}
          className={cn(TYPE.body, 'block font-medium text-ds-text')}
        >
          {name}
        </Link>
        {hasAlert ? (
          <span className="mt-s1 inline-flex items-center gap-s1 text-ds-red-ink">
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
          </span>
        ) : null}
      </span>
    </span>
  );
}

function stopRowInteractive(event: MouseEvent) {
  event.stopPropagation();
}

function onlyMyPatientsStorageKey(userId: string) {
  return `clinio_only_my_patients:${userId}`;
}

function ariaSort(
  sort: PatientListSort,
  column: PatientListSortColumn,
): 'ascending' | 'descending' | 'none' {
  if (sort.column !== column) return 'none';
  return sort.direction === 'asc' ? 'ascending' : 'descending';
}
